import {Button} from '@wandb/weave/components/Button';
import {Select} from '@wandb/weave/components/Form/Select';
import {IconClose} from '@wandb/weave/components/Icon/Icon';
import {IconButton} from '@wandb/weave/components/IconButton';
import {
  Message,
  Messages,
} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/ChatView/types';
import {StyledTextArea} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/StyledTextarea';
import React, {useCallback, useRef} from 'react';

const ROLE_OPTIONS = [
  {value: 'system', label: 'System'},
  {value: 'user', label: 'User'},
  {value: 'assistant', label: 'Assistant'},
];

export interface MessageEditorProps {
  messages: Messages;
  setMessages: (messages: Messages) => void;
  allowDeleteLastMessage?: boolean;
}

export const MessageEditor: React.FC<MessageEditorProps> = ({
  messages,
  setMessages,
  allowDeleteLastMessage = false,
}) => {
  const textAreaRefs = useRef<(HTMLTextAreaElement | null)[]>([]);

  const handleMessageChange = useCallback(
    (index: number, field: 'role' | 'content', value: string) => {
      const updatedMessages = messages.map((msg, i) =>
        i === index ? {...msg, [field]: value} : msg
      );
      setMessages(updatedMessages);
    },
    [messages, setMessages]
  );

  const handleAddMessage = useCallback(() => {
    const newMessage: Message = {role: 'assistant', content: ''};
    const updatedMessages = [...messages, newMessage];
    setMessages(updatedMessages);
  }, [messages, setMessages]);

  const handleRemoveMessage = useCallback(
    (index: number) => {
      // Prevent removing if there's only one message left
      if (messages.length <= 1 && !allowDeleteLastMessage) return;

      const updatedMessages = messages.filter((_, i) => i !== index);
      setMessages(updatedMessages);
    },
    [messages, setMessages, allowDeleteLastMessage]
  );

  return (
    <div className="space-y-8">
      {messages.map((message, index) => (
        <div key={index}>
          <div className="p-8 -mb-[40px] z-10 relative flex items-center justify-between">
            <div style={{width: '100px'}}>
              <Select
                options={ROLE_OPTIONS}
                value={ROLE_OPTIONS.find(
                  option => option.value === message.role
                )}
                onChange={option =>
                  option && handleMessageChange(index, 'role', option.value)
                }
                size="small"
                isSearchable={false}
              />
            </div>
            {(messages.length > 1 || allowDeleteLastMessage) && (
              <IconButton
                className="z-50"
                onClick={() => handleRemoveMessage(index)}>
                <IconClose />
              </IconButton>
            )}
          </div>
          <StyledTextArea
            ref={el => {
              textAreaRefs.current[index] = el;
            }}
            value={typeof message.content === 'string' ? message.content : ''}
            onChange={e =>
              handleMessageChange(index, 'content', e.target.value)
            }
            placeholder={`Enter ${message.role} message...`}
            autoGrow={true}
            startHeight="136px"
            className="min-h-[136px] pt-[40px] px-16 w-full"
          />
        </div>
      ))}

      <Button
        onClick={handleAddMessage}
        variant="secondary"
        startIcon="add-new">
        Add message
      </Button>
    </div>
  );
};
